import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { TrendingUp, TrendingDown, Activity, Info } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { gtiAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Cell } from 'recharts';

const COLORS = ['#b4c5ff', '#4edea3', '#ffb4ab', '#2563eb', '#b9c7df', '#8d90a0'];

const Compare = () => {
  const [allCountries, setAllCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState('all');
  const [timeWindow, setTimeWindow] = useState('12m');

  useEffect(() => {
    fetchGTICountries();
  }, []);

  useEffect(() => {
    if (selectedCountries.length >= 2) {
      fetchComparison();
    } else {
      setComparisonData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountries]);

  const fetchGTICountries = async () => {
    try {
      const res = await gtiAPI.getCountries({ limit: 100, sort: 'rank' });
      const countries = (res.data?.data || []).map(c => ({
        value: c.country_code,
        label: `${c.country_code} — GTI: ${parseFloat(c.gti_score).toFixed(1)}`,
        code: c.country_code,
        gti_score: parseFloat(c.gti_score),
        gti_rank: c.gti_rank,
        event_count: c.event_count,
        trend: c.trend,
      }));
      setAllCountries(countries);
    } catch (error) {
      console.error('Error fetching GTI countries:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchComparison = async () => {
    setLoading(true);
    try {
      const codes = selectedCountries.map(c => c.value);
      const profiles = await Promise.all(
        codes.map(code => gtiAPI.getCountry(code))
      );

      const data = profiles.map((res, idx) => {
        const profile = res.data;
        const country = profile.country;
        const sectors = profile.sectors || [];
        const events = profile.recentEvents || [];

        // Build sector breakdown
        const sectorMap = {};
        sectors.forEach(s => {
          sectorMap[s.sector] = {
            gti: parseFloat(s.gti_score) || 0,
            import: parseFloat(s.import_tariff_score) || 0,
            export: parseFloat(s.export_tariff_score) || 0,
            deals: parseFloat(s.trade_deal_score) || 0,
            dispute: parseFloat(s.dispute_penalty) || 0,
            events: s.event_count || 0,
          };
        });

        return {
          code: country.country_code,
          gtiScore: parseFloat(country.gti_score) || 0,
          gtiRank: country.gti_rank,
          rawScore: parseFloat(country.gti_raw_score) || 0,
          trend: country.trend,
          scoreChange: parseFloat(country.score_change) || 0,
          eventCount: country.event_count || 0,
          sectorCount: country.sector_count || 0,
          topSectors: country.top_sectors,
          sectors: sectorMap,
          recentEvents: events.slice(0, 5),
          color: COLORS[idx % COLORS.length],
        };
      });

      setComparisonData(data);
    } catch (error) {
      console.error('Error fetching comparison data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary-container border-t-transparent dark:border-primary" />
      </div>
    );
  }

  // Build GTI bar chart data
  const gtiBarData = comparisonData?.map(item => ({
    name: item.code,
    'GTI Score': item.gtiScore,
    'Events': item.eventCount,
  })) || [];

  // Build sector comparison radar data
  const allSectors = new Set();
  comparisonData?.forEach(c => Object.keys(c.sectors).forEach(s => allSectors.add(s)));
  const radarData = Array.from(allSectors).map(sector => {
    const entry = { sector };
    comparisonData?.forEach(c => {
      entry[c.code] = Math.abs(c.sectors[sector]?.gti || 0);
    });
    return entry;
  });

  const heatmapSectors = comparisonData?.length
    ? Array.from(
        comparisonData.reduce((set, c) => {
          Object.keys(c.sectors || {}).forEach((s) => set.add(s));
          return set;
        }, new Set())
      ).sort()
    : [];

  const heatmapHasData = comparisonData && comparisonData.length >= 2 && heatmapSectors.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title="GTI Benchmark Analysis | Compare Countries — TradeRadar"
        description="Compare countries side-by-side using the AI-powered Global Trade Index (GTI). Analyze tariff rates, trade deals, sanctions, sector breakdowns, and trade events across multiple nations."
        path="/compare"
        keywords="compare countries trade, global trade index comparison, compare tariffs, compare GTI scores, trade comparison tool, side-by-side trade analysis, country trade comparison"
      />
      <div className="mb-6">
        <p className="label-intel mb-2 text-slate-500 dark:text-outline">Benchmark workspace</p>
        <h1 className="font-headline text-3xl font-bold text-primary-700 dark:text-primary md:text-4xl">
          GTI Benchmark Analysis
        </h1>
        <p className="mt-2 max-w-3xl text-slate-600 dark:text-on-surface-variant">
          Side-by-side Global Trade Index scores, sector matrices, and recent events—built for analysts tracking friction across economies.
        </p>
      </div>

      <div
        className="mb-8 flex flex-wrap items-center gap-3 rounded-3xl border border-outline-variant/15 bg-surface-container-low p-4 dark:bg-surface-container"
        title="Region and time filters are coming soon; comparisons already use the latest GTI snapshot from the API."
      >
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
          <Info className="h-4 w-4 text-primary-600 dark:text-primary" aria-hidden />
          Filters
        </div>
        <select
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
          className="rounded-stitch-lg border border-outline-variant/25 bg-white px-3 py-2 text-sm dark:bg-surface-container-high"
          aria-label="Region (coming soon)"
        >
          <option value="all">All regions</option>
          <option value="apac">Asia-Pacific</option>
          <option value="emea">Europe &amp; MENA</option>
          <option value="americas">Americas</option>
        </select>
        <select
          value={timeWindow}
          onChange={(e) => setTimeWindow(e.target.value)}
          className="rounded-stitch-lg border border-outline-variant/25 bg-white px-3 py-2 text-sm dark:bg-surface-container-high"
          aria-label="Time window (coming soon)"
        >
          <option value="12m">Last 12 months</option>
          <option value="24m">Last 24 months</option>
          <option value="ytd">Year to date</option>
        </select>
        <span className="text-xs text-slate-500 dark:text-on-surface-variant">API uses the latest GTI snapshot; scoped date filters coming soon.</span>
      </div>

      {/* Country Selection */}
      <div className="mb-8 rounded-3xl border border-outline-variant/15 bg-surface-container-low p-6 dark:bg-surface-container">
        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-on-surface-variant">
          Select 2 or more countries to compare (GTI-tracked)
        </label>
        <Select
          isMulti
          options={allCountries}
          value={selectedCountries}
          onChange={setSelectedCountries}
          placeholder="Select countries (e.g. USA, CHN, IND)..."
          className="basic-multi-select"
          classNamePrefix="select"
          noOptionsMessage={() => 'No GTI data available — run the pipeline first'}
        />
        {allCountries.length === 0 && (
          <p className="mt-2 text-sm text-amber-600 dark:text-primary-fixed-dim">
            No GTI data found. Run{' '}
            <code className="rounded bg-slate-100 px-1 dark:bg-surface-container-high">npm run pipeline</code> to
            compute the Global Trade Index.
          </p>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary-container border-t-transparent dark:border-primary" />
        </div>
      )}

      {comparisonData && comparisonData.length >= 2 && (
        <>
          {/* ── GTI Performance Matrix + sector view ── */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
            <div className="rounded-3xl border border-outline-variant/15 bg-surface-container-low p-6 dark:bg-surface-container">
                <h2 className="mb-4 font-headline text-xl font-semibold text-slate-900 dark:text-on-surface">
                  GTI Performance Matrix
                </h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={gtiBarData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="GTI Score" fill="#2563eb">
                    {gtiBarData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Sector Radar Chart */}
            {radarData.length > 0 && (
              <div className="rounded-3xl border border-outline-variant/15 bg-surface-container-low p-6 dark:bg-surface-container">
                <h2 className="mb-4 font-headline text-xl font-semibold text-slate-900 dark:text-on-surface">
                  Twelve-month strategic comparison (sectors)
                </h2>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="sector" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} />
                    {comparisonData.map((c, idx) => (
                      <Radar
                        key={c.code}
                        name={c.code}
                        dataKey={c.code}
                        stroke={c.color}
                        fill={c.color}
                        fillOpacity={0.15}
                      />
                    ))}
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Sector heatmap */}
          {heatmapHasData ? (
            <div className="mb-8 overflow-hidden rounded-3xl border border-outline-variant/15 bg-surface-container-low p-6 dark:bg-surface-container">
              <h2 className="mb-2 font-headline text-xl font-semibold text-slate-900 dark:text-on-surface">
                Sector friction heatmap
              </h2>
              <p className="mb-4 text-xs text-slate-500 dark:text-on-surface-variant">
                Cell color reflects absolute sector GTI contribution (darker = higher friction).
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 dark:text-on-surface-variant">Sector</th>
                      {comparisonData.map((c) => (
                        <th key={c.code} className="px-2 py-2 text-center font-semibold" style={{ color: c.color }}>
                          {c.code}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapSectors.map((sector) => (
                      <tr key={sector} className="border-t border-outline-variant/10">
                        <td className="px-2 py-2 font-medium text-slate-800 dark:text-on-surface">{sector}</td>
                        {comparisonData.map((c) => {
                          const raw = c.sectors[sector]?.gti;
                          const v = raw != null ? Math.abs(raw) : 0;
                          const alpha = Math.min(0.85, 0.12 + v / 120);
                          return (
                            <td key={c.code} className="px-1 py-1 text-center">
                              <div
                                className="rounded-lg px-2 py-2 font-mono font-semibold text-slate-900 dark:text-on-surface"
                                style={{ backgroundColor: `${c.color}${Math.round(alpha * 255)
                                  .toString(16)
                                  .padStart(2, '0')}` }}
                              >
                                {raw != null ? raw.toFixed(1) : '—'}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            comparisonData &&
            comparisonData.length >= 2 && (
              <div className="mb-8 rounded-3xl border border-dashed border-outline-variant/30 bg-surface-container-low/50 p-6 dark:bg-surface-container">
                <h2 className="mb-2 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">
                  Sector heatmap (illustrative)
                </h2>
                <p className="mb-4 text-sm text-slate-600 dark:text-on-surface-variant">
                  Selected countries do not yet share sector coverage in the API response. Below is a neutral layout example—cells will populate once sector rows exist.
                </p>
                <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex h-14 items-center justify-center rounded-lg bg-slate-200/80 text-[10px] font-medium text-slate-500 dark:bg-surface-container-high dark:text-on-surface-variant"
                    >
                      Demo
                    </div>
                  ))}
                </div>
              </div>
            )
          )}

          {/* ── Comparison Table ── */}
          <div className="mb-8 overflow-hidden rounded-3xl border border-outline-variant/15 bg-surface-container-low dark:bg-surface-container">
            <div className="border-b border-outline-variant/15 bg-surface-container px-6 py-4 dark:bg-surface-container-high/40">
              <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">
                Detailed GTI Comparison
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-100 dark:bg-surface-container-low">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                      Metric
                    </th>
                    {comparisonData.map(c => (
                      <th key={c.code} className="px-6 py-3 text-center text-xs font-medium uppercase" style={{ color: c.color }}>
                        {c.code}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100 bg-primary-50 dark:border-outline-variant/10 dark:bg-primary-container/10">
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-on-surface">
                      GTI Score (0-100)
                    </td>
                    {comparisonData.map(c => (
                      <td key={c.code} className="px-6 py-4 text-center">
                        <span
                          className={`text-2xl font-bold ${
                            c.gtiScore >= 50 ? 'text-emerald-600 dark:text-tertiary' : 'text-red-600 dark:text-error'
                          }`}
                        >
                          {c.gtiScore.toFixed(1)}
                        </span>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t border-slate-100 dark:border-outline-variant/10">
                    <td className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-on-surface-variant">Global Rank</td>
                    {comparisonData.map(c => (
                      <td
                        key={c.code}
                        className="px-6 py-4 text-center text-sm font-semibold text-slate-900 dark:text-on-surface"
                      >
                        #{c.gtiRank || '—'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t border-slate-100 dark:border-outline-variant/10">
                    <td className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-on-surface-variant">Trend</td>
                    {comparisonData.map(c => (
                      <td key={c.code} className="px-6 py-4 text-center">
                        {c.trend === 'improving' ? (
                          <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-tertiary">
                            <TrendingUp className="h-4 w-4" /> Improving
                          </span>
                        ) : c.trend === 'declining' ? (
                          <span className="inline-flex items-center gap-1 font-medium text-red-600 dark:text-error">
                            <TrendingDown className="h-4 w-4" /> Declining
                          </span>
                        ) : (
                          <span className="text-slate-400 dark:text-outline">Stable</span>
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t border-slate-100 dark:border-outline-variant/10">
                    <td className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-on-surface-variant">
                      Trade Events
                    </td>
                    {comparisonData.map(c => (
                      <td key={c.code} className="px-6 py-4 text-center text-sm text-slate-900 dark:text-on-surface">
                        {c.eventCount}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t border-slate-100 dark:border-outline-variant/10">
                    <td className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-on-surface-variant">
                      Active Sectors
                    </td>
                    {comparisonData.map(c => (
                      <td key={c.code} className="px-6 py-4 text-center text-sm text-slate-900 dark:text-on-surface">
                        {c.sectorCount}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t border-slate-100 dark:border-outline-variant/10">
                    <td className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-on-surface-variant">
                      Raw GTI Score
                    </td>
                    {comparisonData.map(c => (
                      <td key={c.code} className="px-6 py-4 text-center text-sm text-slate-600 dark:text-on-surface-variant">
                        {c.rawScore.toFixed(2)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Sector Breakdown Per Country ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {comparisonData.map(c => (
              <div key={c.code} className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 dark:border-outline-variant/15 dark:bg-surface-container">
                <h3
                  className="mb-3 border-l-2 pl-3 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface"
                  style={{ borderColor: c.color }}
                >
                  {c.code} — Sector Breakdown
                </h3>
                {Object.keys(c.sectors).length === 0 ? (
                  <p className="text-sm text-slate-400 dark:text-outline">No sector data available</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(c.sectors)
                      .sort(([, a], [, b]) => Math.abs(b.gti) - Math.abs(a.gti))
                      .map(([sector, data]) => (
                        <div
                          key={sector}
                          className="flex items-center justify-between rounded-stitch-lg bg-slate-50 py-2 px-2 dark:bg-surface-container-lowest"
                        >
                          <span className="text-sm font-medium text-slate-700 dark:text-on-surface-variant">
                            {sector}
                          </span>
                          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-on-surface-variant">
                            <span>
                              GTI:{' '}
                              <strong
                                className={
                                  data.gti >= 0 ? 'text-emerald-600 dark:text-tertiary' : 'text-red-600 dark:text-error'
                                }
                              >
                                {data.gti.toFixed(2)}
                              </strong>
                            </span>
                            <span>Deals: {data.deals.toFixed(0)}</span>
                            <span>Disputes: {data.dispute.toFixed(0)}</span>
                            <span className="rounded-stitch bg-slate-200/80 px-2 dark:bg-secondary-container dark:text-on-secondary-container">
                              {data.events} events
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ── Recent Events Per Country ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {comparisonData.map(c => (
              <div key={c.code} className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 dark:border-outline-variant/15 dark:bg-surface-container">
                <h3
                  className="mb-3 border-l-2 pl-3 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface"
                  style={{ borderColor: c.color }}
                >
                  {c.code} — Recent Trade Events
                </h3>
                {c.recentEvents.length === 0 ? (
                  <p className="text-sm text-slate-400 dark:text-outline">No recent events</p>
                ) : (
                  <div className="space-y-3">
                    {c.recentEvents.map(ev => (
                      <div key={ev.id} className="rounded-stitch-lg bg-slate-50 p-3 dark:bg-surface-container-lowest">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            ev.event_type?.includes('deal') ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                            ev.event_type?.includes('sanction') || ev.event_type?.includes('ban') ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}>
                            {ev.event_type?.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-slate-400 dark:text-outline">
                            {ev.created_at ? new Date(ev.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <p className="line-clamp-2 text-sm text-slate-700 dark:text-on-surface-variant">
                          {ev.description || `${ev.sector || ''} — ${ev.product || ''}`}
                        </p>
                        {ev.tariff_rate_percent && (
                          <span className="text-xs font-medium text-amber-700 dark:text-primary-fixed-dim">
                            Tariff: {ev.tariff_rate_percent}%
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {selectedCountries.length < 2 && !loading && (
        <div className="rounded-stitch-xl border border-slate-200/80 bg-white py-12 text-center dark:border-outline-variant/15 dark:bg-surface-container">
          <Activity className="mx-auto mb-4 h-12 w-12 text-slate-300 dark:text-outline" />
          <p className="text-lg text-slate-500 dark:text-on-surface-variant">
            Select at least 2 countries to compare their Global Trade Index.
          </p>
          <p className="mt-2 text-sm text-slate-400 dark:text-outline">
            {allCountries.length > 0
              ? `${allCountries.length} countries available with GTI data`
              : 'Run the pipeline first to compute GTI scores'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Compare;
